import { useSyncExternalStore } from "react";
import { setBoard, STICKERS, newPace, type BoardId, type Question, type Comfort, type PaceState } from "@numi/engine";
import { api, ApiError } from "./api.ts";

export type Screen = "loading" | "landing" | "auth" | "kids" | "home" | "syllabus" | "topic" | "learn" | "play" | "bubbles" | "done" | "stickers" | "vedic" | "wrap";
export type VoiceGender = "auto" | "warm" | "bright";
export interface Child { id: string; name: string; grade: number; board: BoardId; lang: string; settings: { autoRead?: boolean | null; calm?: boolean | null; voiceGender?: VoiceGender | null; friendBadge?: boolean; comfort?: Comfort } }
export interface Prog { level: 1 | 2 | 3; streak: number; miss: number; right: number; seen: number; stars: number; last?: number; lastRound?: number | null;
  /** Outcomes of the last 10 questions, oldest first ("1" = right first time, "0" = not). Mastery is judged on this, so it can fade. */
  recent?: string }
/** Records one question outcome in the rolling window. */
export function pushRecent(p: Prog, ok: boolean) { p.recent = ((p.recent || "") + (ok ? "1" : "0")).slice(-10); }
export interface Doubt { id: string; tid: string | null; kind: string; q: string; resolved: boolean | null; at: number }
export interface Feedback { cls: "" | "good" | "warn"; text: string }
export interface Stuck { kind: "q" | "start" | "diff" | null; msgs: { t: string; me?: boolean }[]; turns: { role: "user" | "assistant"; content: string }[]; busy: boolean; answered: boolean }
export interface QState { ctx: "try" | "play"; q: Question; typed: string; tries: number; done: boolean; revealed: boolean; feedback: Feedback; stuck: Stuck | null; aiBubble: string | null; story: string | null; doubtIds: string[];
  /** When the question appeared / was finished, and how many hints were used: what pacing listens to. */ t0?: number; doneAt?: number; hints?: number }
export interface Round { combo?: number; n: number; right: number; marks: string[]; queue?: { tid: string; level: 1 | 2 | 3; label?: string }[]; len?: number;
  /** Plain text of questions already asked this round, so one round never repeats a question. */
  seen?: string[];
  /** Topics the child missed this round; one of them returns as the last question. */
  retry?: string[] }
export interface Result { kind: "round" | "bubble"; right?: number; of?: number; prev?: number | null; score?: number; stars?: number; calm?: boolean; sticker: number | null; mixed: boolean; brk?: boolean }
export interface PlanPrice { amount: number; list?: number; days: number; months: number; label: string }
export interface Billing { status: "free" | "trial" | "active" | "expired"; trialEndsAt: number; paidUntil: number | null; plan: string | null; autoRenew: boolean; subStatus: string | null; market: "IN" | "AE";
  pricing: Record<"IN" | "AE", { currency: string; symbol: string; locale: string; plans: Record<"m1" | "m3", PlanPrice> }>; payReady: boolean; keyId: string | null }
export interface Bub { /** Optional topic ids to draw from when mixed (e.g. only Vedic tricks). */ pool: string[] | null; label: string | null; mixed: boolean; calm: boolean; score: number; end: number; q: Question; opts: string[]; topic: string | null; tid: string; starsWon: number }
export interface ChatMsg { role: "user" | "assistant"; content: string; img?: string; err?: boolean }

export const S = {
  screen: "loading" as Screen,
  parent: null as null | { id: string; email: string; name: string; hasPin: boolean; phone?: string | null; wa?: boolean; weekly?: boolean },
  children: [] as Child[],
  child: null as Child | null,
  progress: {} as Record<string, Prog>,
  totalStars: 0, todayStars: 0,
  doubts: [] as Doubt[],
  topic: null as string | null,
  /** The door (section) the child is browsing, so Back returns to it. */
  section: null as null | "syllabus" | "vedic",
  step: 0,
  qs: null as QState | null,
  round: null as Round | null,
  result: null as Result | null,
  pendingSticker: null as number | null,
  easier: false,
  storyMode: false,
  storyCallsThisSession: 0,   // BUG-013: cap story AI calls to prevent quota drain
  rounds: 1,  // start at 1 so first round (rounds++ → 1) doesn't immediately satisfy rounds%3===0
  chat: [] as ChatMsg[],
  chatOpen: false,
  grownOpen: false,
  toast: "",
  offline: false,
  spokenKey: "",
  spokenQ: null as Question | null,
  bub: null as null | Bub,
  billing: null as Billing | null,
  grownTab: "" as "" | "plans",
  /** A free Vedic Maths trick opened from a nudge email (works after the trial too). */
  trickPeek: null as string | null,
  authMode: "signup" as "signup" | "login",
  /** Grown-ups corner stays unlocked for a few minutes after a check. */
  grownUntil: 0,
  plan: null as null | import("./plan.ts").Plan,
  planRunning: null as number | null,
  planJustDone: null as number | null,
  /** Pacing: how the current session is going (resets when a child is picked). */
  pace: newPace() as PaceState,
  /** A stretch break on screen: from the comfort timer or from pacing. */
  breakOpen: null as null | "timer" | "pace",
  /** Active seconds since the last break (for the comfort break timer). */
  sinceBreak: 0,
};

let version = 0; const subs = new Set<() => void>();
export function touch() { version++; subs.forEach((f) => f()); }
export function set(p: Partial<typeof S>) { Object.assign(S, p); touch(); }
export function useApp() { useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => version); return S; }
export function toast(msg: string) { set({ toast: msg }); setTimeout(() => { if (S.toast === msg) set({ toast: "" }); }, 2600); }

export const autoRead = () => S.child?.settings.autoRead ?? ((S.child?.grade ?? 3) <= 2);
export const calmMode = () => S.child?.settings.calm ?? ((S.child?.grade ?? 3) <= 2);
export const voiceGender = () => S.child?.settings.voiceGender ?? "auto";
/** The child's learning comfort switches (all off unless a grown-up turned them on). */
export const comfort = (): Comfort => S.child?.settings.comfort || {};
/** Puts the comfort switches that change the whole page onto <html>, where the stylesheet picks them up. */
export function applyComfort() {
  const c = comfort(), d = document.documentElement.dataset;
  c.bigText ? (d.text = "big") : delete d.text;
  c.lessMotion ? (d.motion = "less") : delete d.motion;
}
export const kid = () => S.child?.name || "friend";
export const stickersOwned = () => Math.min(STICKERS.length, Math.floor(S.totalStars / 5));
export function prog(tid: string): Prog { return S.progress[tid] || (S.progress[tid] = { level: 1, streak: 0, miss: 0, right: 0, seen: 0, stars: 0 }); }

/* ---------- offline-friendly event queue ---------- */
type Ev = Record<string, unknown> & { type: string };
const qKey = () => `numi.queue.${S.child?.id}`;
function loadQ(): Ev[] { try { return JSON.parse(localStorage.getItem(qKey()) || "[]"); } catch { return []; } }
function saveQ(q: Ev[]) { try { localStorage.setItem(qKey(), JSON.stringify(q.slice(-500))); } catch {} }
let flushTimer: number | undefined, flushing = false;
export function queue(ev: Ev) { if (!S.child) return; const q = loadQ(); q.push({ ...ev, at: Date.now() }); saveQ(q); clearTimeout(flushTimer); flushTimer = window.setTimeout(flush, 800); }
export async function flush() {
  if (!S.child || flushing) return; const q = loadQ(); if (!q.length) return; flushing = true;
  const batch = q.slice(0, 200);
  try { await api(`/children/${S.child.id}/events`, { body: { events: batch } }); saveQ(loadQ().slice(batch.length)); if (S.offline) set({ offline: false }); if (loadQ().length) setTimeout(flush, 50); }
  catch (e) { if (e instanceof ApiError && e.code === "offline") set({ offline: true }); }
  finally { flushing = false; }
}
window.addEventListener("online", () => flush());
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
setInterval(() => { if (S.offline) flush(); }, 15000);

export const saveProg = (tid: string) => queue({ type: "progress", topicId: tid, data: prog(tid) });
export function addStars(n: number) {
  const before = stickersOwned();
  S.totalStars += n; S.todayStars += n;
  if (stickersOwned() > before) S.pendingSticker = stickersOwned() - 1;
  queue({ type: "stars", delta: n }); touch();
}
export function logDoubt(kind: string, text: string, tid?: string | null): string {
  const d: Doubt = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, tid: tid ?? (S.qs?.q.tid as string | undefined) ?? S.topic, kind, q: String(text).slice(0, 90), resolved: kind === "asked" || kind === "photo" ? null : false, at: Date.now() };
  S.doubts = [d, ...S.doubts].slice(0, 60);
  queue({ type: "doubt", id: d.id, topicId: d.tid, kind, text: d.q, resolved: d.resolved });
  return d.id;
}
export function resolveDoubts(ids: string[]) { if (!ids.length) return; S.doubts.forEach((d) => { if (ids.includes(d.id)) d.resolved = true; }); queue({ type: "resolve", ids }); }

/* ---------- navigation ---------- */
let screenHooks: (() => void)[] = [];
export function onLeave(f: () => void) { screenHooks.push(f); }
export function go(screen: Screen) {
  // leaving a round part-way through is one of the signals pacing listens to
  if (S.screen === "play" && screen !== "play" && screen !== "done" && S.round && S.round.n > 0) S.pace.quits++;
  screenHooks.forEach((f) => f()); screenHooks = []; if (screen === "home" || screen === "kids" || screen === "vedic" || screen === "syllabus") { S.planRunning = null; S.planJustDone = null; } if (screen === "home" || screen === "kids") S.section = null; S.screen = screen; window.scrollTo({ top: 0 }); touch(); }
export function back() {
  // Topics return to the door they were opened from; Abacus and lesson steps return home.
  const fromTopic: Screen = S.planRunning != null ? "home" : S.topic === "abacus" ? "home" : S.topic?.startsWith("v-") ? "vedic" : "syllabus";
  const fromMixed: Screen = S.section || "home";
  const m: Partial<Record<Screen, Screen>> = { topic: fromTopic, syllabus: "home", vedic: "home", wrap: "home", stickers: S.section || "home", learn: "topic", play: "topic", bubbles: S.result?.mixed || !S.topic ? fromMixed : "topic", done: S.result?.mixed ? fromMixed : "topic", home: "kids" };
  go(m[S.screen] || "home");
}

/* ---------- session ---------- */
export async function loadBilling() { try { S.billing = await api("/billing"); touch(); } catch {} }
export async function boot() {
  try {
    const me = await api("/auth/me");
    S.parent = me.parent; S.children = me.children; await loadBilling();
    const last = localStorage.getItem("numi.child");
    if (last && S.children.some((c) => c.id === last)) await selectChild(last); else go("kids");
    await nudgeLanding();
  } catch (e) {
    if (e instanceof ApiError && e.code === "offline") set({ offline: true });
    const q = new URLSearchParams(location.search);
    if (q.has("signin")) { S.authMode = "login"; go("auth"); } else if (q.has("signup")) { S.authMode = "signup"; go("auth"); } else go("landing");
  }
}
/** Where a parent lands after tapping a button in a trial-expiry email: the free trick, or the plans. */
async function nudgeLanding() {
  const q = new URLSearchParams(location.search), src = q.get("src") || "";
  if (src === "weekly") { // weekly report email: straight to the grown-ups corner (it still asks for the grown-up check)
    history.replaceState({}, "", location.pathname);
    if (!S.child && S.children.length === 1) await selectChild(S.children[0].id);
    if (S.child) { S.grownOpen = true; touch(); }
    return;
  }
  if (!src.startsWith("nudge")) return;
  history.replaceState({}, "", location.pathname);
  if (src === "nudgeback") { if (!S.child && S.children.length === 1) await selectChild(S.children[0].id); return; } // "come back" reminder: straight to play
  const trick = q.get("trick");
  if (trick) { S.trickPeek = trick; touch(); return; }
  if (!S.child && S.children.length === 1) await selectChild(S.children[0].id);
  S.grownTab = "plans"; S.grownOpen = true; touch();
}
export async function selectChild(id: string) {
  await flush();
  const st = await api(`/children/${id}/state`);
  S.child = st.child; S.progress = st.progress; S.totalStars = st.totalStars; S.todayStars = st.todayStars; S.doubts = st.doubts;
  S.chat = []; S.topic = null; S.qs = null; S.pace = newPace(); S.sinceBreak = 0; S.breakOpen = null;
  setBoard(st.child.board); applyComfort();
  try { localStorage.setItem("numi.child", id); } catch {}
  flush(); go("home");
}
export async function refreshMe() { const me = await api("/auth/me"); S.parent = me.parent; S.children = me.children; touch(); }
export async function updateChild(p: Partial<Child>) {
  if (!S.child) return;
  Object.assign(S.child, p); if (p.board) setBoard(p.board);
  applyComfort(); touch();
  await api(`/children/${S.child.id}`, { method: "PATCH", body: p }).catch((e) => toast(e.message));
  refreshMe().catch(() => {});
}
export async function signOut() { await flush(); await api("/auth/logout", { body: {} }).catch(() => {}); try { localStorage.removeItem("numi.child"); } catch {} Object.assign(S, { parent: null, children: [], child: null, grownOpen: false, grownUntil: 0, billing: null, authMode: "login" }); go("auth"); }
